import React, { FC, ReactNode, useMemo, useState } from "react";
import { Box, Button } from "@mui/material";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import FormFile from "../../components/Forms/FormFile";

import TranslationWrapper from "../../components/TranslationWrapper/TranslationWrapper";
import {
  defaultDropAnimationSideEffects,
  DndContext,
  DragOverlay,
  DropAnimation,
  MouseSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { rectSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { createPortal } from "react-dom";
import { restrictToVerticalAxis, restrictToWindowEdges } from "@dnd-kit/modifiers";

type FileType = "image" | "file";

export const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: "0.5",
      },
    },
  }),
};

export interface MediaUrl {
  conversions: { [key: string]: { url: string; srcset?: string } };
  original: string;
  id: number;
  order_column: number;
  mime_type: string;
}

export default function ImageUuids({
  fileType = "image",
  columnName = "img_uuids",
  collectionName = "imgid",
}: {
  fileType?: FileType;
  columnName?: string;
  collectionName?: string;
}) {
  const { control } = useFormContext();

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const { fields, append, move } = useFieldArray({
    control,
    name: columnName,
  });
  const mediaImgId: MediaUrl[] | undefined = useWatch({ name: `media_urls.${collectionName}` });
  const activeIndex = useMemo(() => {
    return fields.findIndex((field) => field.id === activeId);
  }, [activeId, fields]);
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 5 } }));

  return (
    <Box>
      <DndContext
        modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
        onDragEnd={(result) => {
          if (result.over?.id) {
            const overIndex = fields.findIndex((field) => field.id === result.over?.id);
            move(activeIndex, overIndex);
          }
          setActiveId(null);
        }}
        onDragStart={(event) => {
          setActiveId(event.active.id);
        }}
        sensors={sensors}
      >
        <SortableContext items={fields} strategy={rectSortingStrategy}>
          <Box>
            {fields.map((field, otherIndex) => {
              let index = mediaImgId?.findIndex((f) => f.id == field.uuid) ?? -1;
              if (index < 0) {
                index = otherIndex;
              }

              return (
                <SortableItem field={field} key={field.id}>
                  <FormFile
                    imgurl={mediaImgId?.[index]?.original}
                    name={`${columnName}.${index}.uuid`}
                    nullable={true}
                    srcSet={
                      mediaImgId?.[index]?.conversions?.responsive?.srcset ??
                      mediaImgId?.[index]?.conversions?.optimized?.srcset
                    }
                    type={fileType}
                  />
                </SortableItem>
              );
            })}
          </Box>
        </SortableContext>
        {createPortal(
          <DragOverlay adjustScale={false} dropAnimation={dropAnimationConfig}>
            {activeIndex >= 0 ? (
              <FormFile
                imgurl={mediaImgId?.[activeIndex]?.original}
                name={`${columnName}.${activeIndex}.uuid`}
                nullable={true}
                srcSet={
                  mediaImgId?.[activeIndex]?.conversions?.responsive?.srcset ??
                  mediaImgId?.[activeIndex]?.conversions?.optimized?.srcset
                }
                type={fileType}
              />
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>

      <Button
        color={"primary"}
        onClick={() => {
          append({ uuid: null });
        }}
        variant={"contained"}
      >
        <TranslationWrapper tKey={"Add new image"} />
      </Button>
    </Box>
  );
}

const SortableItem: FC<{ field: any; children: ReactNode }> = ({
  field,
  children,
}: {
  field: any;
  children: ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: field.id });

  const style = transform
    ? {
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: 1,
      }
    : undefined;

  return (
    <Box ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {children}
    </Box>
  );
};
